import axios from 'axios';
import config from '../config/environment';
import { authService } from './authService';

export const EXPERIMENT_ID = 'PITRU_PAKSHA_V2_PREMIUM';
export const qualificationStatuses = ['NEW', 'QUALIFIED', 'NOT_QUALIFIED', 'NEEDS_REVIEW'] as const;
export const consultationStatuses = ['REQUESTED', 'CONTACT_ATTEMPTED', 'SCHEDULED', 'COMPLETED', 'NO_SHOW', 'CANCELLED'] as const;
export const offerStatuses = ['NOT_OFFERED', 'OFFERED', 'DECLINED', 'ACCEPTED_PENDING_PAYMENT'] as const;
export const objections = ['TRUST', 'PRICE', 'VALUE', 'SACRED_LOCATION_NOT_IMPORTANT', 'TIMING', 'FAMILY_DECISION', 'PREFERS_OFFLINE_PANDIT', 'COMPETITOR_OR_ALTERNATIVE', 'DOES_NOT_BUY_THIS_TYPE_OF_SERVICE', 'OTHER'] as const;
export interface Attribution {
  source?: string; medium?: string; campaignName?: string; term?: string; content?: string;
  landingPage?: string; referralUrl?: string;
}
export interface LeadAnswers {
  name: string; email: string; phone: string; countryOfResidence: string;
  ritualIntent: string; preferredSacredLocation: string; wantsLocationGuidance: string; knowsTithi: string;
  expectedTimeframe: string; priceConsideration: string; broadNeed?: string | null;
}
export interface LeadSubmission extends LeadAnswers, Attribution { submissionId: string; recaptchaToken: string; }
export interface LeadReceipt { id: string; createdAt: string; }
export interface LeadUpdate {
  version: number;
  qualificationStatus: typeof qualificationStatuses[number];
  consultationStatus: typeof consultationStatuses[number];
  offerStatus: typeof offerStatuses[number];
  primaryObjection: typeof objections[number] | null;
  operatorNotes: string | null;
}
export interface PitruLead extends LeadAnswers, Attribution, LeadUpdate, LeadReceipt {
  updatedAt: string; experimentId: string; consultationCompletedAt?: string;
}
export interface LeadPage { content: PitruLead[]; totalPages: number; totalElements: number; }

const adminApi = axios.create({ baseURL: config.authServerUrl });
adminApi.interceptors.request.use(request => {
  const token = localStorage.getItem('access_token');
  if (token) request.headers.Authorization = `Bearer ${token}`;
  return request;
});
adminApi.interceptors.response.use(response => response, async error => {
  const request = error.config;
  if (error.response?.status === 401 && request && !request._retry) {
    request._retry = true;
    await authService.refreshAccessToken();
    return adminApi(request);
  }
  return Promise.reject(error);
});

export const pitruLeadService = {
  async submit(body: LeadSubmission): Promise<LeadReceipt> {
    const response = await axios.post(`${config.authServerUrl}/public/pitru-v2/leads`, body);
    return response.data;
  },
  async list(page: number): Promise<LeadPage> {
    return (await adminApi.get('/api/admin/pitru-v2/leads', { params: { page, size: 25 } })).data;
  },
  async get(id: string): Promise<PitruLead> {
    return (await adminApi.get(`/api/admin/pitru-v2/leads/${id}`)).data;
  },
  async update(id: string, body: LeadUpdate): Promise<PitruLead> {
    return (await adminApi.put(`/api/admin/pitru-v2/leads/${id}`, body)).data;
  }
};

export function leadError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 403) return 'Administrator access is required.';
    if (error.response?.status === 401) return 'Please sign in again.';
    if (error.response?.status === 409) return 'The record changed or is being saved. Reload the lead or retry your request.';
    if (error.response?.status === 429) return 'Too many requests. Please wait a minute and try again.';
    if (!error.response || error.response.status >= 500) return 'We could not confirm the request. Please try again; your answers are still here.';
    return 'Please check your answers and try again. Verification may have expired.';
  }
  return 'We could not complete the request. Please try again.';
}
